import type { CompanySearchResult } from '@dash-lite/sdk';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { CompanyPreview } from '@/components/company/company-preview';
import { Breadcrumb, Page } from '@/components/layout/page';
import { Checkout } from '@/components/payment/checkout';
import { DevNote } from '@/components/ui/dev-note';
import { Alert, Container, Section } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { paths } from '@/lib/paths';

/**
 * Page de paiement — la deuxième et dernière étape du parcours.
 *
 * Le visiteur y arrive **directement depuis la recherche** : il a choisi une
 * entreprise, il voit son aperçu public à gauche et saisit sa carte à droite.
 * Aucune page intermédiaire ne s'intercale, et aucune donnée payante n'est
 * demandée avant le paiement — l'aperçu tient entièrement dans ce que
 * `GET /companies/search` renvoie gratuitement.
 *
 * La page assemble aussi le **contexte d'achat** transmis à `POST /payment`.
 *
 * Ce contexte (`metadata`) mérite qu'on s'y arrête : l'API est sans état, elle
 * ne sait pas ce que votre client était en train de faire. Le champ permet de
 * le lui dire, et de le retrouver ensuite sur le droit d'accès — sans tenir sa
 * propre table de correspondance. Ici, on y met l'entreprise que le visiteur
 * cherchait à consulter, ce qui permet de le renvoyer au bon endroit après
 * l'achat.
 *
 * Limites imposées par Stripe, à qui le contexte est transmis : 10 clés au
 * plus, valeurs de 200 caractères. Et une règle non négociable : ce contexte
 * est visible dans le back-office et dans le compte Stripe du site — **jamais
 * de données personnelles ou confidentielles**.
 */
export default function CheckoutPage() {
    const site = useSite();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { hasAccess } = useSession();

    /** L'entreprise que le visiteur voulait consulter, si elle est connue. */
    const registrationNumber = searchParams.get('entreprise');

    /**
     * Aperçu transporté depuis la liste de résultats : il évite un second
     * appel à la recherche et affiche l'entreprise sans le moindre délai.
     */
    const preview = (location.state as { preview?: CompanySearchResult } | null)?.preview ?? null;

    // Un visiteur qui a déjà un accès actif n'a rien à acheter : on l'envoie
    // directement à ce qu'il était venu consulter plutôt que de créer un
    // paiement inutile.
    if (hasAccess) {
        return <Navigate to={registrationNumber ? paths.company(registrationNumber) : paths.account} replace />;
    }

    if (!site.offer) {
        return (
            <Page title="Paiement">
                <Container className="py-20">
                    <Alert tone="caution" title="Aucune offre n'est configurée">
                        Ce site n'a pas encore défini son offre : il n'y a rien à acheter pour le moment.
                    </Alert>
                </Container>
            </Page>
        );
    }

    const metadata: Record<string, string> = {
        origin: 'site-exemple-docinfopro',
        ...(registrationNumber ? { registration_number: registrationNumber } : {}),
    };

    return (
        <Page title={preview?.name ?? 'Finaliser votre accès'}>
            <Container className="py-8">
                <Breadcrumb
                    items={[
                        { label: 'Accueil', to: paths.home },
                        { label: 'Recherche', to: paths.search },
                        { label: preview?.name ?? registrationNumber ?? 'Paiement' },
                    ]}
                />
            </Container>

            <Section className="py-0 pb-16">
                <Container className="space-y-8">
                    <div className="grid items-start gap-8 lg:grid-cols-[1fr_1.05fr]">
                        <CompanyPreview registrationNumber={registrationNumber} initial={preview} />

                        <Checkout metadata={metadata} />
                    </div>

                    <DevNote
                        title="Ce que fait cet écran"
                        endpoints={['GET /companies/search', 'POST /payment', 'POST /payment/claim']}
                    >
                        <p>
                            Deux appels cohabitent ici, et c'est tout l'intérêt de l'écran. La colonne de gauche
                            n'utilise que la <strong>recherche publique</strong> : pas de token, pas de droit d'accès,
                            aucun crédit fournisseur consommé au-delà de la recherche elle-même. La colonne de droite
                            ouvre le paiement.
                        </p>
                        <p>
                            La fiche complète (<code className="font-mono">GET /companies/{'{id}'}</code>) n'est jamais
                            appelée avant l'ouverture de l'accès : les rubriques annoncées à gauche ne sont pas masquées
                            en CSS, elles n'ont simplement pas été demandées.
                        </p>
                        {registrationNumber && (
                            <p>
                                L'identifiant <code className="font-mono">{registrationNumber}</code> est joint au
                                paiement dans <code className="font-mono">metadata.registration_number</code>. Il
                                reviendra sur le droit d'accès (<code className="font-mono">GET /entitlement</code>),
                                ce qui permet de ramener le visiteur sur la bonne fiche après l'achat.
                            </p>
                        )}
                    </DevNote>
                </Container>
            </Section>
        </Page>
    );
}
